/**
 * NotificationService - Servis za upravljanje notifikacijama
 * 
 * Omogućava komunikaciju sa Notification API endpoint-ima:
 * - POST /notification/search - Pretraga notifikacija
 * - POST /notification/read-toggle - Promena statusa pročitanosti
 */

import ApiService from '@/core/api/ApiService';
import { API_ENDPOINTS } from '@/core/api/endpoints';

/** In-flight zahtevi po read flagu – istovremeni search(true) ili search(false) dele jedan poziv. */
const searchInFlight = { true: null, false: null };

/** Keš rezultata po read flagu – TTL 2 min. Osvežava se pri otvaranju taba/focus/toggleRead. */
const CACHE_TTL_MS = 2 * 60 * 1000;
const searchCache = { true: null, false: null };

class NotificationService {
  /**
   * Pretražuje notifikacije. Prvo keš (ako svež), zatim in-flight deduplikacija.
   * @param {boolean} read - Da li da prikaže samo pročitane (true) ili nepročitane (false)
   * @param {{ forceRefresh?: boolean }} options - forceRefresh: uvek dohvati sveže (za otvaranje modala/taba)
   * @returns {Promise<Array>} Lista notifikacija
   */
  async search(read = false, options = {}) {
    const key = read === true ? 'true' : 'false';
    if (options.forceRefresh) {
      searchCache.true = null;
      searchCache.false = null;
    }
    const cached = searchCache[key];
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      return cached.data;
    }
    if (searchInFlight[key]) {
      return searchInFlight[key];
    }
    const promise = this._doSearch(read);
    searchInFlight[key] = promise;
    promise
      .then((data) => {
        searchCache[key] = { data, timestamp: Date.now() };
      })
      .catch(() => {
        // Rejection se prosleđuje pozivaocu preko originalnog promise-a;
        // ovde ga "konzumiramo" da interna side-effect grana ne pravi unhandled rejection.
      })
      .finally(() => { searchInFlight[key] = null; });
    return promise;
  }

  /** Invalidira keš notifikacija (posle toggleRead, pri otvaranju modala/taba ili focus prozora). */
  invalidateSearchCache() {
    searchCache.true = null;
    searchCache.false = null;
  }

  async _doSearch(read = false) {
    try {
      const payload = typeof read === 'boolean' ? { read } : {};
      const response = await ApiService.post(
        API_ENDPOINTS.NOTIFICATION.SEARCH,
        payload
      );
      const data = response.data;
      if (!data) return [];
      if (Array.isArray(data.notifications)) return data.notifications;
      if (Array.isArray(data.items)) return data.items;
      if (Array.isArray(data.results)) return data.results;
      if (Array.isArray(data.data)) return data.data;
      if (Array.isArray(data)) return data;
      return [];
    } catch (error) {
      if (error.response && error.response.status === 401) {
        throw new Error('Корисник није пријављен');
      }
      if (error.response && error.response.status >= 500) {
        throw new Error('Сервис за обавештења тренутно није доступан. Покушајте поново.');
      }
      if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
        throw new Error('Сервис за обавештења тренутно није доступан. Покушајте поново.');
      }
      throw error;
    }
  }

  /**
   * Menja status pročitanosti notifikacije
   * @param {string} notificationId - ID notifikacije
   * @returns {Promise<Object>} Odgovor servera
   * @throws {Error} Ako API poziv ne uspe
   * 
   * @example
   * await NotificationService.toggleRead('3fa85f64-5717-4562-b3fc-2c963f66afa6');
   */
  async toggleRead(notificationId) {
    try {
      const response = await ApiService.post(
        API_ENDPOINTS.NOTIFICATION.READ_TOGGLE,
        { notification_id: notificationId }
      );
      this.invalidateSearchCache();
      // API vraća odgovor u response.data
      return response.data || { success: true };
    } catch (error) {
      // Ako je 401, korisnik nije prijavljen
      if (error.response && error.response.status === 401) {
        throw new Error('Корисник није пријављен');
      }
      
      // Ako je 404, notifikacija ne postoji
      if (error.response && error.response.status === 404) {
        throw new Error('Обавештење није пронађено');
      }
      
      // Za ostale greške, baci originalnu grešku
      throw error;
    }
  }
}

// Singleton pattern - kreiranje jedne instance
const notificationServiceInstance = new NotificationService();
export default notificationServiceInstance;
